<template>
  <!-- 🔧 懒加载：只有在 shouldLoadImage 为 true 时才真正加载图片 -->
  <template v-if="hasValidSrc && shouldLoadImage">
    <van-image :src="showGif ? srcGif : src" :alt="alt" :width="width" :height="height" :radius="radius" :round="round"
      :lazy-load="lazyLoad" :show-error="showError" :show-loading="showLoading" :error-icon="errorIcon"
      :loading-icon="loadingIcon" :icon-prefix="iconPrefix" :icon-size="iconSize" :fit="fit" :position="position"
      :cross-origin="crossOrigin" :referrer-policy="referrerPolicy" :block="block" @load="handleLoad"
      @error="handleError" @click="handleClick" :class="imageClass" :style="imageStyle"
      loading="lazy" decoding="async">
      <!-- 加载中插槽：优先使用父级自定义，否则显示骨架屏 -->
      <template v-slot:loading>
        <slot v-if="$slots.loading" name="loading" />
        <div v-else-if="customLoading" class="skeleton-container" :style="skeletonStyle">
          <div class="skeleton-image"></div>
          <div class="skeleton-content"></div>
        </div>
      </template>

      <!-- 错误插槽：优先使用父级自定义，否则显示默认错误 UI -->
      <template v-slot:error>
        <div v-if="customError" class="app-image-error">
          <div class="error-content">
            <img src="@/assets/images/other/sitemap.png" class="w-full h-full" />
          </div>
        </div>
      </template>
    </van-image>
    <!-- 预加载 GIF -->
    <img v-if="srcGif && !gifLoaded" :src="srcGif" style="display:none" @load="handleGifLoad" @error="handleGifError" />
  </template>
  <!-- 🔧 懒加载：未进入视口时显示占位骨架屏 -->
  <template v-else-if="!shouldLoadImage">
    <div :class="imageClass" :style="imageStyle" @click="handleClick">
      <div v-if="customLoading" class="skeleton-container" :style="skeletonStyle">
        <div class="skeleton-image"></div>
        <div class="skeleton-content"></div>
      </div>
    </div>
  </template>
  <!-- 无效 src 时显示错误状态 -->
  <template v-else>
    <div :class="imageClass" :style="imageStyle" @click="handleClick">
      <div v-if="customError" class="app-image-error">
        <div class="error-content">
          <img src="@/assets/images/other/sitemap.png" class="w-full h-full" />
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import type { ImageFit } from 'vant'
import theme from '@/styles/theme'

interface Props {
  // VanImage 原生属性
  srcGif?: string
  src?: string
  alt?: string
  width?: string | number
  height?: string | number
  radius?: string | number
  round?: boolean
  lazyLoad?: boolean
  showError?: boolean
  showLoading?: boolean
  errorIcon?: string
  loadingIcon?: string
  iconPrefix?: string
  iconSize?: string | number
  fit?: ImageFit
  position?: string
  crossOrigin?: string
  referrerPolicy?: string
  block?: boolean

  // 自定义属性
  customLoading?: boolean // 是否使用自定义加载状态
  customError?: boolean // 是否使用自定义错误状态
  errorText?: string // 自定义错误文本
  aspectRatio?: string // 宽高比
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down' // CSS object-fit
  borderRadius?: string | number // 自定义圆角
  shadow?: boolean // 是否显示阴影
  placeholder?: string // 占位图
  fallback?: string // 加载失败后的备用图
  // 🔧 懒加载控制
  isVisible?: boolean // 是否在视口内（由父组件控制）
  eager?: boolean // 是否立即加载（跳过懒加载）
}

interface Emits {
  load: [event: Event]
  error: [event: Event]
  click: [event: Event]
}

const props = withDefaults(defineProps<Props>(), {
  lazyLoad: true,
  showError: true,
  showLoading: false,
  customLoading: true,
  customError: true,
  errorText: '图片加载失败',
  fit: 'cover',
  aspectRatio: '',
  borderRadius: 8,
  shadow: false,
  isVisible: undefined, // 默认 undefined，表示不使用懒加载控制
  eager: false // 默认不立即加载
})

const emit = defineEmits<Emits>()

// 内部状态
const isLoaded = ref(false)
const hasError = ref(false)
const gifLoaded = ref(false)

// 计算：是否应该显示 GIF（先展示静态图，等 GIF 加载完成后再显示 GIF）
const showGif = computed(() => {
  // 如果有 GIF 且已加载完成，才显示 GIF
  return props.srcGif && gifLoaded.value
})

// 计算：当前是否有可用的图片地址（src 或 srcGif）
const hasValidSrc = computed(() => !!((props.src || props.srcGif) && String(props.src || props.srcGif).trim()))

// 🔧 计算：是否应该真正加载图片（懒加载控制）
const shouldLoadImage = computed(() => {
  // 如果设置了 eager，立即加载
  if (props.eager) return true
  
  // 如果没有传入 isVisible（undefined），默认加载
  if (props.isVisible === undefined) return true
  
  // 否则根据 isVisible 决定
  return props.isVisible
})

// 监听 srcGif 变化，重置 GIF 加载状态
watch(() => props.srcGif, () => {
  gifLoaded.value = false
})

// GIF 加载完成
const handleGifLoad = () => {
  gifLoaded.value = true
}

// GIF 加载失败
const handleGifError = () => {
  gifLoaded.value = false
}

// 计算样式
const imageClass = computed(() => [
  'app-image',
  {
    'app-image--shadow': props.shadow,
    'app-image--round': props.round,
    'app-image--loaded': isLoaded.value,
    'app-image--error': hasError.value
  }
])

const imageStyle = computed(() => {
  const styles: Record<string, any> = {}

  // 宽高比
  if (props.aspectRatio) {
    styles.aspectRatio = props.aspectRatio
  }

  // 自定义圆角
  if (props.borderRadius && !props.round) {
    styles.borderRadius = typeof props.borderRadius === 'number'
      ? `${props.borderRadius}px`
      : props.borderRadius
  }

  // object-fit
  if (props.objectFit) {
    styles.objectFit = props.objectFit
  }

  // 确保图片容器有正确的尺寸
  if (props.width) {
    styles.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }

  if (props.height) {
    styles.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }

  return styles
})

const skeletonStyle = computed(() => ({
  width: props.width ? (typeof props.width === 'number' ? `${props.width}px` : props.width) : '100%',
  height: props.height ? (typeof props.height === 'number' ? `${props.height}px` : props.height) : '200px',
  borderRadius: props.round ? '50%' : (
    typeof props.borderRadius === 'number'
      ? `${props.borderRadius}px`
      : props.borderRadius
  )
}))

// 事件处理
const handleLoad = (event: Event) => {
  isLoaded.value = true
  hasError.value = false
  emit('load', event)
}

const handleError = (event: Event) => {
  hasError.value = true
  isLoaded.value = false

  // 如果有备用图，尝试加载备用图
  // if (props.fallback && currentSrc.value !== props.fallback) {
  //   currentSrc.value = props.fallback
  //   return
  // }

  emit('error', event)
}

const handleClick = (event: Event) => {
  emit('click', event)
}

onMounted(() => {
  nextTick(() => {
    const img = document.querySelector('.van-image__img') as HTMLImageElement
    if (img && img.complete) {
      isLoaded.value = true
      hasError.value = false
    } else if (img) {
      img.addEventListener('load', () => {
        isLoaded.value = true
        hasError.value = false
      })
    }
  })
})

// 暴露给父组件的方法
defineExpose({
  reload: () => {
    hasError.value = false
    isLoaded.value = false
    gifLoaded.value = false
  }
})
</script>

<style lang="scss" scoped>

:deep(.van-image__error) {
  background-color: transparent !important;
}

.app-image {
  position: relative;
  overflow: hidden;
  background-color: v-bind('theme.colors.background');
  transition: all 0.3s ease;

  &--shadow {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &--round {
    border-radius: 50%;
  }

  &--loaded {
    background-color: transparent;
  }


  // 加载状态
  .app-image-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: v-bind('theme.colors.background');

    .loading-skeleton {
      background: linear-gradient(90deg,
          rgba(255, 255, 255, 0.04) 0%,
          rgba(255, 255, 255, 0.10) 50%,
          rgba(255, 255, 255, 0.04) 100%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
    }
  }

  // 错误状态
  .app-image-error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    // background-color: var(--van-gray-1);

    .error-content {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;

      .error-text {
        font-size: 12px;
        color: var(--van-text-color-3);
        text-align: center;
      }
    }
  }
}

// 骨架屏动画
@keyframes skeleton-loading {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

// 深色主题适配
.van-theme-dark .app-image {
  background-color: v-bind('theme.colors.background');

  .app-image-loading {
    background: v-bind('theme.colors.background');
  }

  .app-image-error {
    background-color: v-bind('theme.colors.background');
  }
}

// 骨架屏效果
.skeleton-container {
  width: 100%;
  height: 100%;
  padding: 0; // 移除padding让骨架屏撑满
  display: flex;
  flex-direction: column;
  gap: 0; // 移除gap让骨架屏撑满
  background-color: v-bind('theme.colors.background');
}

.skeleton-image {
  width: 100%;
  height: 100%; // 撑满整个容器
  background: linear-gradient(90deg,
      rgba(255, 255, 255, 0.06) 25%,
      rgba(255, 255, 255, 0.12) 50%,
      rgba(255, 255, 255, 0.06) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: inherit; // 继承父容器的圆角
  min-height: unset; // 移除最小高度限制
  background-color: v-bind('theme.colors.background');
}

.skeleton-content {
  display: none; // 隐藏内容区域，只保留主要的骨架屏
}
</style>
